import * as React from 'react';

import {
    type NavigationScreenState,
    resolveLocationToNavigationScreen,
} from '@gitbook/app-navigation';
import { confirm } from '@gitbook/spine-react';
import { useBeforeUnload, useLiveRef } from '@gitbook/util-react-hooks';

import { useHistory, useHistoryLocation } from './history';

/**
 * Show a modal to prevent navigating away from the current screen.
 */
export function BlockNavigationModal(props: {
    block: boolean;
    title?: string;
    confirmLabel?: string;
    description?: string;
    acceptTransition?: (
        currentScreen: NavigationScreenState,
        nextScreen: NavigationScreenState
    ) => boolean;
    onConfirm?: () => void;
}) {
    const { block, description } = props;

    const acceptTransitionRef = useLiveRef(props.acceptTransition);
    const history = useHistory();
    const location = useHistoryLocation();

    useBeforeUnload(() => (block ? (description ?? 'You have unsaved changes') : null));

    const propsRef = useLiveRef(props);

    // biome-ignore lint/correctness/useExhaustiveDependencies: see comment about `location` in the effect
    React.useEffect(() => {
        if (!block) {
            return;
        }

        const acceptTransition = acceptTransitionRef.current;

        return history.block(
            (current, next) => {
                if (acceptTransition) {
                    const currentScreen = resolveLocationToNavigationScreen(current);
                    const nextScreen = resolveLocationToNavigationScreen(next);

                    if (acceptTransition(currentScreen, nextScreen)) {
                        return false;
                    }

                    return true;
                }

                return true;
            },
            (blockTransaction) => {
                if (blockTransaction) {
                    confirm({
                        title: propsRef.current.title ?? 'Unsaved changes, leave anyway?',
                        description:
                            propsRef.current.description ??
                            'You have unsaved changes. Do you want to leave anyway? Changes that you made may not be saved.',
                        confirmLabel: propsRef.current.confirmLabel ?? 'Leave',
                        kind: 'danger',
                        onConfirm: () => {
                            propsRef.current.onConfirm?.();
                            blockTransaction.unblock();
                        },
                    });
                }
            }
        );
        // We are adding `location` to the deps to make sure this effect runs whenever the location changes.
        // This is important because we need to check if the block should be applied to the new location.
    }, [block, location, history, acceptTransitionRef, propsRef]);

    return null;
}
